import { useApi } from '@src/app/context/Api';
import { ListCodesData } from '@src/entities/Code/includes/types';
import { configuration } from '@src/entities/Event/includes/configuration';
import {
  DefaultEventEntity,
  DefaultListEventsPayload,
  EventEntity,
  EventTypeCode,
  ListEventsData,
  ListEventsPayload,
  ListEventTypesData,
  ListEventTypesItem,
} from '@src/entities/Event/includes/types';
import { EventsTable } from '@src/entities/Event/ui/components';
import { Modal } from '@src/entities/Event/ui/components/Modal';
import { User } from '@src/entities/User/ui/components/User';
import { CreateButton } from '@src/features/Events/Create';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { Loader } from '@src/shared/ui/components/Loader';
import { useAssets } from '@src/shared/ui/hooks/useAssets';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { motion } from 'framer-motion';
import { isUndefined } from 'lodash';
import React, { useEffect, useState } from 'react';

type Assets = {
  ListEventTypes?: {
    data?: ListEventTypesData;
  };
  ListImpactedSystems?: {
    data?: ListCodesData;
  };
};

/**
 * Page: List Events
 *
 * Provides the user with a screen listing events.
 */
export function List() {
  const [showModal, setShowModal] = useState<boolean>(false);
  const [currentEntity, setCurrentEntity] = useState<EventEntity>(DefaultEventEntity);
  const [requestArgs, setRequestArgs] = useState<ListEventsPayload>(DefaultListEventsPayload);

  const { useApiQuery } = useApi('Event');

  const { assets, isLoadingAssets }: { assets: Assets; isLoadingAssets: boolean } = useAssets([
    {
      apiKey: 'Event',
      queryKey: 'ListEventTypes',
    },
    {
      apiKey: 'Source',
      queryKey: 'ListImpactedSystems',
    },
  ]);

  const [ListEvents, { data, error, isFetching, isLoading, refetch }] = useApiQuery('ListEvents') as [
    (args: ListEventsPayload) => void,
    {
      data?: ListEventsData;
      error?: unknown;
      isFetching: boolean;
      isLoading: boolean;
      refetch: () => void;
    },
  ];

  /**
   * Updates the event type code list in the list events request once the event types are loaded.
   */
  useEffect(() => {
    if (!isUndefined(assets?.ListEventTypes?.data)) {
      const eventTypeCodeList =
        assets?.ListEventTypes?.data?.map((eventType) => {
          return eventType?.codeId as EventTypeCode;
        }) || [];

      if (eventTypeCodeList !== requestArgs?.eventTypeCodeList) {
        setRequestArgs({ ...requestArgs, eventTypeCodeList });
      }
    }
  }, [assets?.ListEventTypes?.data]);

  return (
    <ErrorHandler location="src/pages/private/Notifications/Events/List/List.tsx">
      <motion.div
        animate="open"
        initial={{ opacity: 0 }}
        variants={{
          closed: { opacity: 0 },
          open: { opacity: 1 },
        }}
      >
        <Grid>
          <Grid.Col
            span={{
              xs: '100%',
            }}
          >
            <Flex alignItems="center" direction="row">
              <Heading offset={0}>{configuration.pluralName}</Heading>
              <CreateButton refetch={refetch}>Create</CreateButton>
            </Flex>
          </Grid.Col>
          <Grid.Col
            span={{
              xs: '100%',
            }}
          >
            {isUndefined(assets?.ListEventTypes?.data) ? (
              <Loader verticalAlignment="start" />
            ) : (
              [
                <EventsTable
                  assets={assets}
                  error={error}
                  isLoading={isLoading || isFetching || isLoadingAssets}
                  key={`${configuration.slug}-table`}
                  requestArgs={{
                    ...requestArgs,
                    eventTypeCodeList: assets?.ListEventTypes?.data?.map((eventType: ListEventTypesItem) => {
                      return eventType?.codeId as EventTypeCode;
                    }),
                  }}
                  requestFunction={ListEvents}
                  requestKey="ListEvents"
                  rows={data?.content || []}
                  setCurrentEntity={setCurrentEntity}
                  setRequestArgs={setRequestArgs}
                  setShowModal={setShowModal}
                  totalPages={data?.totalPages || 1}
                  totalRecords={data?.totalElements || 0}
                />,
                <User.Capability attributes={['edit']} key="user-capability" resource="Notifications:Events">
                  <Modal
                    action="edit"
                    currentEntity={currentEntity}
                    isOpen={showModal}
                    refetch={refetch}
                    setCurrentEntity={setCurrentEntity}
                    setIsOpen={setShowModal}
                  />
                </User.Capability>,
              ]
            )}
          </Grid.Col>
        </Grid>
      </motion.div>
    </ErrorHandler>
  );
}
